/* original source: https://ui.shadcn.com/docs/components/alert */

import { Slot } from './Slot'
import { cn, tv, createNonNullableContext } from '@joyful/utils'
import type { HTMLMotionProps } from 'motion/react'
import { m } from 'motion/react'
import type { VariantProps } from '@joyful/utils'
import type { ComponentProps } from 'react'

export const alertVariants = tv({
  base: "relative grid grid-cols-[minmax(0,max-content)_1fr] [grid-template-areas:'icon_content'_'actions_actions'] sm:flex w-full items-start gap-x-2 has-[>[data-slot=alert-actions]]:gap-y-1 rounded-md",

  compoundVariants: [
    {
      chrome: 'inset',
      class: {
        base: 'border-tremor-brand',
      },
      variant: 'warning',
    },
  ],

  defaultVariants: {
    chrome: 'boxed',
    size: 'md',
    variant: 'default',
  },

  slots: {
    content: '[grid-area:content]',
    description: 'gap-1 text-tremor-content-emphasis [&_p]:leading-relaxed',
    icon: 'mt-0.5',
    title: 'min-h-4 font-semibold tracking-tight',
  },

  variants: {
    chrome: {
      boxed: {
        base: 'border',
      },
      inset: {
        base: 'rounded-none border-y',
      },
    },
    size: {
      md: {
        base: 'p-4',
        description: 'text-sm',
        icon: 'size-5',
        title: 'text-base',
      },
      sm: {
        base: 'p-3',
        description: 'text-xs',
        icon: 'size-4',
        title: 'text-base',
      },
    },
    variant: {
      default: {
        base: 'bg-tremor-background text-tremor-content-strong',
        icon: 'text-current',
      },
      error: {
        base: 'border-red-500 bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-400 dark:border-red-600/50',
        icon: 'text-red-500 dark:text-red-400',
      },
      info: {
        base: 'border-blue-500/30 bg-blue-500/10 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        icon: 'text-blue-600 dark:text-blue-400',
      },
      neutral: {
        base: 'border-tremor-border bg-tremor-background-subtle text-tremor-content-strong',
        icon: 'text-tremor-content',
      },
      success: {
        base: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
        icon: 'text-emerald-500 dark:text-emerald-400',
      },
      warning: {
        base: 'border-tremor-brand-subtle bg-tremor-brand-faint text-tremor-brand-emphasis',
        icon: 'text-tremor-brand-emphasis',
      },
    },
  },
})

const alertContentVariants = tv({
  base: 'flex flex-[1_0_0] flex-col gap-y-1',

  variants: {
    gap: {
      lg: 'gap-3',
      md: 'gap-2.5',
      sm: 'gap-2',
    },
    layout: {
      responsive: 'sm:flex-row sm:items-center sm:justify-between',
    },
  },
})

const alertDescriptionVariants = tv({
  variants: {
    align: {
      center: 'text-center',
    },
    layout: {
      inline: 'flex items-center gap-2',
      stacked: 'flex flex-col gap-y-2',
    },
    weight: {
      medium: 'font-medium',
    },
  },
})

const AlertClassNamesContext = createNonNullableContext<
  ReturnType<typeof alertVariants>
>('AlertClassNamesContext')

export type AlertProps = Omit<HTMLMotionProps<'div'>, 'className'> &
  VariantProps<typeof alertVariants> &
  Pick<ComponentProps<'div'>, 'children'>

export function Alert({ chrome, size, variant, ...props }: AlertProps) {
  const classNames = alertVariants({
    chrome,
    size,
    variant,
  })

  return (
    <AlertClassNamesContext.Context value={classNames}>
      <m.div
        data-slot="alert"
        role="alert"
        className={classNames.base()}
        {...props}
      />
    </AlertClassNamesContext.Context>
  )
}

export type AlertIconContainerProps = Omit<
  ComponentProps<typeof Slot>,
  'className'
>

export function AlertIconContainer(props: AlertIconContainerProps) {
  const classNames = AlertClassNamesContext.useRequiredContext()

  return <Slot aria-hidden="true" className={classNames.icon()} {...props} />
}

export interface AlertContentProps
  extends
    Omit<ComponentProps<'div'>, 'className'>,
    VariantProps<typeof alertContentVariants> {}

export function AlertContent({
  children,
  gap,
  layout,
  ...props
}: AlertContentProps) {
  const classNames = AlertClassNamesContext.useRequiredContext()

  return (
    <div
      className={cn(
        alertContentVariants({ gap, layout }),
        classNames.content(),
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export type AlertTitleProps = Omit<ComponentProps<'div'>, 'className'>

export function AlertTitle(props: AlertTitleProps) {
  const classNames = AlertClassNamesContext.useRequiredContext()

  return (
    <div data-slot="alert-title" className={classNames.title()} {...props} />
  )
}

export interface AlertDescriptionProps
  extends
    Omit<ComponentProps<'div'>, 'className'>,
    VariantProps<typeof alertDescriptionVariants> {}

export function AlertDescription({
  align,
  layout,
  weight,
  ...props
}: AlertDescriptionProps) {
  const classNames = AlertClassNamesContext.useRequiredContext()

  return (
    <div
      data-slot="alert-description"
      className={cn(
        classNames.description(),
        alertDescriptionVariants({ align, layout, weight }),
      )}
      {...props}
    />
  )
}

export interface AlertActionsProps extends Omit<
  ComponentProps<'div'>,
  'className'
> {
  /**
   * @default false
   */
  asChild?: boolean
}

export function AlertActions({ asChild, ...props }: AlertActionsProps) {
  const Component = asChild ? Slot : 'div'

  return (
    <Component
      data-slot="alert-actions"
      className="[grid-area:actions]"
      {...props}
    />
  )
}
